import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation, LANGUAGES } from '../../utils/i18n';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ContactFormModal } from '../ContactFormModal';
import { AdminViewModeSwitch } from '../AdminViewModeSwitch';
import { 
  LayoutDashboard, Car, Settings, LogOut, 
  Users, Package, Database, BarChart3,
  FileText, Shield, User as UserIcon, Cog,
  Image as ImageIcon, Mail, Receipt, Megaphone, FileCheck, Truck, Activity, Sparkles, Cookie, Download, Upload, Book, HelpCircle, Gift, TrendingUp, MessageSquare, Bot, Brain, Hash, Bug, Wifi, ShoppingCart, Server, ChevronDown, Globe, Menu, X, Zap, Edit3
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const GlobalSidebar = () => {
  const { user, logout, locale, switchLocale, viewMode, setViewMode, subscription, hasFeature } = useAuth();
  const { t } = useTranslation(locale);
  const navigate = useNavigate();
  const location = useLocation();
  const [simulatedPlan, setSimulatedPlan] = useState('unlimited');
  const [branding, setBranding] = useState({ logo: null, favicon: null });
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const langDropdownRef = useRef(null);

  // Subscription is now loaded from AuthContext
  const subscriptionLoaded = subscription !== null || user === null;

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const response = await axios.get(`${API}/admin/branding/public`);
        setBranding(response.data);
      } catch (error) {
        console.error('Failed to fetch branding');
      }
    };
    
    fetchBranding();
  }, []);

  // Close language dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target)) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Explicit check to prevent any type coercion issues
  const isAdmin = user?.is_admin === true;
  const isAdminView = isAdmin && viewMode === 'admin';
  
  // Don't show conditional menu items until subscription is loaded
  const showConditionalItems = subscriptionLoaded;

  // Menu Structure with Categories
  // Admin categories only show when in Admin view mode (not when simulating user)
  const menuCategories = [
    {
      title: null, // No title for main menu
      items: [
        { icon: LayoutDashboard, label: t('dashboard'), path: '/dashboard', show: true },
        { icon: Car, label: t('myVehicles'), path: '/vehicles', show: true },
        { icon: Database, label: 'Fahrzeugdaten-Bibliothek', path: '/vehicle-library', show: isAdminView || (showConditionalItems && (subscription?.features?.vehicle_data_library === true || subscription?.features?.extended_vehicle_database === true)) },
        { icon: Hash, label: 'VIN-Decoder', path: '/vin-decoder', show: isAdminView || (showConditionalItems && subscription?.features?.vin_decoder === true) },
        { icon: Activity, label: 'OBD Live', path: '/obd-live', show: isAdminView || (showConditionalItems && subscription?.features?.obd_live_diagnosis === true) },
        { icon: FileText, label: t('history'), path: '/history', show: true },
        { icon: Download, label: 'Downloads', path: '/downloads', show: isAdminView || (showConditionalItems && subscription?.features?.obd_live_diagnosis === true) },
        { icon: Gift, label: 'Empfehlungen', path: '/referrals', show: true },
        { icon: Sparkles, label: 'Tuning-Konfigurator', path: '/tuning-configurator', show: isAdminView || (showConditionalItems && (subscription?.features?.tuning_configurator === true || subscription?.features?.tuning_data === true)) },
        { icon: Receipt, label: 'Rechnungen', path: '/invoices', show: showConditionalItems && subscription?.has_subscription === true },
        { icon: Package, label: t('subscription'), path: '/subscription', show: true },
        { icon: Settings, label: 'Einstellungen', path: '/settings', show: true },
      ]
    },
    {
      title: 'Hilfe & Support',
      show: true,
      items: [
        { icon: Book, label: 'Benutzerhandbuch', path: '/manual', show: true },
        { icon: HelpCircle, label: 'FAQ', path: '/faq', show: true },
        { icon: Mail, label: 'Support Kontaktieren', action: 'openContactModal', show: true },
      ]
    },
    {
      title: 'Live OBD System',
      show: isAdminView, // Only show in Admin view mode
      items: [
        { icon: Wifi, label: 'Live OBD Agents', path: '/admin/live-obd', show: isAdminView },
        { icon: Package, label: 'Dongle Shop', path: '/admin/dongle-shop', show: isAdminView, badge: 'NEU' },
      ]
    },
    {
      title: 'Diagnose & Pläne',
      show: isAdminView, // Only show in Admin view mode
      items: [
        { icon: Activity, label: 'Diagnose-Pläne', path: '/admin/diagnose-plans', show: isAdminView },
      ]
    },
    {
      title: 'FMS Flottenverwaltung',
      show: isAdminView,
      items: [
        { icon: Truck, label: 'FMS-Pläne', path: '/admin/fms-plans', show: isAdminView },
        { icon: Truck, label: 'FMS Einstellungen', path: '/admin/fms-settings', show: isAdminView },
      ]
    },
    {
      title: 'Fahrzeugdaten',
      show: isAdminView,
      items: [
        { icon: Database, label: 'Fahrzeug-Datenbank', path: '/admin/vehicle-master', show: isAdminView, badge: 'ZENTRAL' },
        { icon: Edit3, label: 'Fahrzeug Editor', path: '/admin/vehicle-editor', show: isAdminView, badge: 'NEU' },
        { icon: Zap, label: 'Data Pipeline', path: '/admin/data-pipeline', show: isAdminView },
        { icon: TrendingUp, label: 'Wissens-Dashboard', path: '/admin/knowledge-dashboard', show: isAdminView },
        { icon: FileText, label: 'RAG Dokumente', path: '/admin/documents', show: isAdminView },
      ]
    },
    {
      title: 'KI Assistenten',
      show: isAdminView,
      items: [
        { icon: Brain, label: 'CarCortex Agent', path: '/admin/carcortex', show: isAdminView, badge: 'NEU' },
      ]
    },
    {
      title: 'Marketing & Social Media',
      show: isAdminView,
      items: [
        { icon: Bot, label: 'Social Media KI', path: '/admin/marketing-ki', show: isAdminView, badge: 'NEU' },
        { icon: Megaphone, label: 'Newsletter', path: '/admin/marketing', show: isAdminView },
        { icon: Sparkles, label: 'Aktionen & Rabatte', path: '/admin/earlybird', show: isAdminView },
        { icon: Sparkles, label: 'Handbuch/FAQ Vorschläge', path: '/admin/content-suggestions', show: isAdminView },
      ]
    },
    {
      title: 'Feedback & E-Mail',
      show: isAdminView,
      items: [
        { icon: MessageSquare, label: 'Feedback-Analyse', path: '/admin/feedback-analysis', show: isAdminView },
        { icon: Mail, label: 'E-Mail Einstellungen', path: '/admin/email', show: isAdminView },
      ]
    },
    {
      title: 'System & Verwaltung',
      show: isAdminView, // Only show in Admin view mode
      items: [
        { icon: Users, label: 'Benutzer', path: '/admin/users', show: isAdminView },
        { icon: Cookie, label: 'Cookie-Consents', path: '/admin/cookie-consents', show: isAdminView },
        { icon: ImageIcon, label: 'Branding & Design', path: '/admin/branding', show: isAdminView },
        { icon: FileCheck, label: 'Disclaimer Audit', path: '/admin/disclaimer', show: isAdminView },
        { icon: Bug, label: 'Debug & Logs', path: '/admin/debug', show: isAdminView },
        { icon: Server, label: 'System & Server', path: '/admin/system', show: isAdminView },
        { icon: BarChart3, label: 'Besucherstatistik', path: '/admin/analytics', show: isAdminView },
      ]
    }
  ];

  const getSessionsRemaining = () => {
    if (!subscription?.has_subscription) return 0;
    if (simulatedPlan === 'unlimited') return 999;
    if (simulatedPlan === 'basic') return Math.max(0, 10 - (subscription?.subscription?.sessions_used_this_period || 0));
    if (simulatedPlan === 'pro') return Math.max(0, 50 - (subscription?.subscription?.sessions_used_this_period || 0));
    return subscription?.sessions_remaining || 0;
  };

  return (
    <div 
      className="fixed left-0 top-0 h-screen w-64 flex flex-col"
      style={{ backgroundColor: '#0B0F14', borderRight: '1px solid #1F2937', zIndex: 50 }}
    >
      <div className="p-6">
        <div 
          className="cursor-pointer flex items-center justify-center"
          onClick={() => navigate('/dashboard')}
        >
          {branding.logo ? (
            <img 
              src={branding.logo} 
              alt="Carcortex-ai Logo" 
              style={{ height: '100px', width: 'auto', maxWidth: '100%' }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextElementSibling.style.display = 'block';
              }}
            />
          ) : null}
          <h1 
            className="text-2xl font-bold"
            style={{ 
              color: '#7CD4FD', 
              fontFamily: 'Manrope',
              display: branding.logo ? 'none' : 'block'
            }}
          >
            Carcortex-ai
          </h1>
        </div>
        
        {!isAdminView && (
          <div className="mt-2 text-xs px-2 py-1 rounded" style={{ backgroundColor: '#22C55E20', color: '#22C55E' }}>
            {getSessionsRemaining()} Sessions übrig
          </div>
        )}
      </div>
      
      {/* Admin View Mode Switch */}
      <AdminViewModeSwitch />
      
      {/* Language Switcher */}
      <div className="px-4 py-2 relative" ref={langDropdownRef}>
        <button
          onClick={() => setIsLangOpen(!isLangOpen)}
          className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all text-sm"
          data-testid="language-switcher"
        >
          <div className="flex items-center gap-2">
            <span className="text-base">{LANGUAGES[locale]?.flag || '🌐'}</span>
            <span className="text-white font-medium">{locale?.toUpperCase() || 'DE'}</span>
          </div>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isLangOpen ? 'rotate-180' : ''}`} />
        </button>

        {isLangOpen && (
          <div className="absolute left-4 right-4 top-full mt-1 py-1 rounded-lg bg-[#1a1f2e] border border-white/10 shadow-xl z-50">
            {Object.values(LANGUAGES).map((lang) => (
              <button
                key={lang.code}
                onClick={() => {
                  switchLocale(lang.code);
                  setIsLangOpen(false);
                }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-white/5 transition-colors ${
                  locale === lang.code ? 'bg-cyan-500/10 text-cyan-400' : 'text-gray-300'
                }`}
                data-testid={`lang-option-${lang.code}`}
              >
                <span className="text-lg">{lang.flag}</span>
                <span className="flex-1">{lang.name}</span>
                {locale === lang.code && (
                  <svg className="w-4 h-4 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
      
      <nav className="px-3 flex-1 overflow-y-auto">
        {menuCategories.map((category, catIndex) => {
          // Don't show category if it has no visible items or category itself is hidden
          if (category.show === false) return null;
          
          const visibleItems = category.items.filter(item => item.show);
          if (visibleItems.length === 0) return null;
          
          return (
            <div key={catIndex} className="mb-6">
              {category.title && (
                <div 
                  className="px-4 py-2 mb-1"
                  style={{ 
                    color: '#E6EDF3',
                    fontSize: '11px',
                    fontWeight: '700',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    opacity: 0.7
                  }}
                >
                  {category.title}
                </div>
              )}
              <div className="space-y-1">
                {visibleItems.map((item) => (
                  <button
                    key={item.path || item.action}
                    onClick={() => {
                      if (item.action === 'openContactModal') {
                        setIsContactModalOpen(true);
                      } else if (item.path) {
                        navigate(item.path);
                      }
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors"
                    style={{ 
                      backgroundColor: location.pathname === item.path ? '#1EA0E620' : 'transparent',
                      color: '#E6EDF3'
                    }}
                  >
                    <item.icon className="w-5 h-5" style={{ color: '#7CD4FD' }} />
                    <span className="text-sm flex-1 text-left">{item.label}</span>
                    {item.badge && (
                      <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-pink-500/30 text-pink-300 border border-pink-400/40">
                        {item.badge}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </nav>
      
      <button
        onClick={logout}
        className="m-6 flex items-center gap-3 px-4 py-3 rounded-lg transition-colors"
        style={{ backgroundColor: '#1F2937', color: '#E6EDF3' }}
      >
        <LogOut className="w-5 h-5" />
        <span>{t('logout')}</span>
      </button>

      {/* Contact Form Modal */}
      <ContactFormModal 
        isOpen={isContactModalOpen} 
        onClose={() => setIsContactModalOpen(false)} 
      />
    </div>
  );
};
